import { useMemo } from 'react'
import { ContentfulLivePreview } from '@contentful/live-preview'

import { Container } from '../../components/container'
import { useBreakpoints } from '../../hooks/use-breakpoints'
import { CmsColor } from '../../models/cms-color'
import { cn } from '../../utils/cn'
import { ButtonCms, ButtonCmsModel } from '../button/button.cms'
import { RichText } from '../helper-components/rich-text'

interface TextBlockCmsProps {
  id: string
  isH1?: boolean
  title?: any
  text: any
  /** Should the title be on the top, right, or left of the text (mobile is always above) */
  orientation?: 'stacked' | 'right' | 'left' | 'stacked-center'
  color?: CmsColor
  padding?: boolean
  button?: ButtonCmsModel
  secondaryButton?: ButtonCmsModel
  /** Text will automatically wrap to this number of columns on desktop */
  numberOfColumns?: number
  mobileNumberOfColumns?: number
}

export const TextBlockCms = (props: TextBlockCmsProps) => {
  const { isMobile } = useBreakpoints()

  const orientationClass = useMemo(() => {
    switch (props.orientation) {
      case 'left':
        return 'flex gap-3 sm:gap-12 sm:flex-row flex-col'
      case 'right':
        return 'flex gap-3 sm:gap-12 sm:flex-row-reverse flex-col'
      case 'stacked-center':
        return 'flex flex-col gap-3 text-center'
      case 'stacked':
      default:
        return 'flex flex-col gap-3'
    }
  }, [props.orientation])

  return (
    <Container
      className={cn(props.padding && 'mx-auto p-4 sm:p-12', 'scroll-mt-25')}
      id={`${props.id}`}
    >
      <div
        style={{
          backgroundColor: props.color?.backgroundColor || '',
          color: props.color?.textColor || '',
        }}
        className="max-w-[1260px] mx-auto"
      >
        <section
          className={cn(
            orientationClass,
            'gap-6 py-6',
            props.color?.backgroundColor &&
              props.color?.backgroundColor !== '#FFFFFF' &&
              'px-8 py-14 sm:py-8'
          )}
          data-testid="section"
        >
          {props.title && (
            <div className="flex-1">
              <h2
                data-testid="text-block-title"
                aria-level={props.isH1 ? 1 : 2}
                className="text-3xl font-medium sm:text-4xl"
                {...ContentfulLivePreview.getProps({
                  entryId: props.id,
                  fieldId: 'title',
                  locale: 'en-US',
                })}
              >
                <div style={{ color: props.color?.textColor || '' }}>
                  <RichText
                    json={props.title?.json}
                    links={props.title?.links}
                  />
                </div>
              </h2>
            </div>
          )}
          {(props.text || props.button || props.secondaryButton) && (
            <div className="flex-[2]">
              <div
                data-testid="text"
                {...ContentfulLivePreview.getProps({
                  entryId: props.id,
                  fieldId: 'text',
                  locale: 'en-US',
                })}
              >
                <RichText
                  style={{
                    columnCount: isMobile
                      ? props.mobileNumberOfColumns || undefined
                      : props.numberOfColumns || undefined,
                    columnGap: '2rem',
                  }}
                  json={props.text?.json}
                  links={props.text?.links}
                />

                {(props.button || props.secondaryButton) && (
                  <div
                    className={cn(
                      'flex flex-wrap gap-3 lg:pb-3',
                      props.orientation === 'stacked-center'
                        ? 'justify-center'
                        : 'justify-start',
                      props.text ? 'mt-6' : ''
                    )}
                  >
                    {props.button && (
                      <ButtonCms
                        content={props.button}
                        className="w-full lg:w-auto"
                        backgroundColor={props.color?.backgroundColor}
                      />
                    )}

                    {props.secondaryButton && (
                      <ButtonCms
                        content={props.secondaryButton}
                        className="w-full lg:w-auto"
                        backgroundColor={props.color?.backgroundColor}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </Container>
  )
}
